import { createFileRoute } from '@tanstack/react-router'

import { JellyIcon } from '@/components/icons'
import { AppButton, NetworkBadge, SectionLabel, Toggle } from '@/components/shared'
import { AppLink as Link } from '@/components/shell'
import { ACCOUNT, NETWORKS, NOTIFICATIONS, TEAM } from '@/mocks/klient/profilNastaveni'

export const Route = createFileRoute('/klient/profil/nastaveni')({
  head: () => ({ meta: [{ title: 'Nastavení · Aplikace Bitez' }] }),
  component: KlientNastaveni,
})

function KlientNastaveni() {
  return (
    <div className="px-4 pt-4 pb-6">
      <Link
        href="/klient/profil"
        className="-ml-1 flex w-fit items-center gap-1.5 rounded-full px-1 py-1 text-[10px] font-bold tracking-widest text-ink/40 uppercase transition-colors duration-150 hover:text-ink/70"
      >
        <JellyIcon name="arrow-left" className="size-3" />
        Profil
      </Link>

      <h1 className="font-display mt-3 text-2xl leading-[0.9] uppercase">Nastavení</h1>

      {/* The logged-in person had no representation anywhere in the app — and
          the login itself hangs on this phone number (spec phase 1 auth). */}
      <section className="animate-rise-up mt-6">
        <SectionLabel>Váš účet</SectionLabel>
        <div className="mt-3 space-y-2.5 rounded-3xl bg-white p-5 shadow-sm">
          {(
            [
              { label: 'Jméno', value: ACCOUNT.name },
              { label: 'Telefon', value: ACCOUNT.phone },
              { label: 'E-mail', value: ACCOUNT.email },
            ] as const
          ).map((row) => (
            <p key={row.label} className="flex items-baseline gap-1.5 text-sm">
              <span className="text-ink/70">{row.label}</span>
              <span aria-hidden className="min-w-4 flex-1 border-b-2 border-dotted border-ink/15" />
              <span className="truncate font-bold">{row.value}</span>
            </p>
          ))}
          <div className="pt-1.5">
            <AppButton variant="quiet" className="w-full">
              Upravit údaje
            </AppButton>
          </div>
        </div>
      </section>

      <section className="animate-rise-up mt-8" style={{ animationDelay: '30ms' }}>
        <SectionLabel>Připojené sítě</SectionLabel>
        <div className="mt-3 divide-y divide-ink/5 rounded-3xl bg-white px-5 shadow-sm">
          {NETWORKS.map((network) => (
            <div key={network.name} className="flex items-center gap-3 py-4">
              <span className="shrink-0">
                <NetworkBadge network={network} size="size-5" ring="ring-white" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold">{network.name}</span>
                <span className="block truncate text-[10px] font-semibold text-ink/45">
                  {network.handle}
                </span>
              </span>
              <button
                type="button"
                className="shrink-0 cursor-pointer text-[10px] font-bold text-ink/40 transition-colors duration-150 hover:text-brand"
              >
                Odpojit
              </button>
            </div>
          ))}
        </div>
        <p className="mt-2.5 text-[10px] leading-relaxed font-semibold text-ink/45">
          Bez připojené sítě nemůžeme publikovat. Přístup máme jen k firemnímu profilu, k soukromým
          účtům ani zprávám ne.
        </p>
      </section>

      <section className="animate-rise-up mt-8" style={{ animationDelay: '60ms' }}>
        <SectionLabel>Váš tým</SectionLabel>
        <div className="mt-3 space-y-3.5">
          {TEAM.map((member) => (
            <div key={member.name} className="flex items-center gap-3">
              <span
                aria-hidden
                className="initial font-display flex size-10 shrink-0 items-center justify-center rounded-full bg-brand text-cream"
              >
                {member.letter}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold">{member.name}</span>
                <span className="block truncate text-[10px] font-semibold text-ink/45">
                  {member.note}
                </span>
              </span>
            </div>
          ))}
          <AppButton variant="quiet" className="w-full">
            <JellyIcon name="plus" className="size-3.5" />
            Přidat člena týmu
          </AppButton>
        </div>
        <p className="mt-2.5 text-[10px] leading-relaxed font-semibold text-ink/45">
          Členové týmu vidí i schvalují to co vy. Kdo co udělal, poznáte v historii.
        </p>
      </section>

      <section className="animate-rise-up mt-8" style={{ animationDelay: '120ms' }}>
        <SectionLabel>Notifikace</SectionLabel>
        <div className="mt-3 space-y-4 rounded-3xl bg-white p-5 shadow-sm">
          {NOTIFICATIONS.map((notification) => (
            <Toggle
              key={notification.label}
              label={notification.label}
              note={notification.note}
              on={notification.on}
            />
          ))}
        </div>
      </section>

      <section className="animate-rise-up mt-8" style={{ animationDelay: '180ms' }}>
        <SectionLabel>Dokumenty</SectionLabel>
        <div className="mt-3 space-y-1">
          {['Obchodní podmínky', 'Ochrana osobních údajů'].map((document) => (
            <button
              key={document}
              type="button"
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-2xl px-2 py-3 text-left text-sm font-semibold text-ink/70 transition-colors duration-150 hover:bg-white"
            >
              <JellyIcon name="file" className="size-4 text-ink/45" />
              {document}
              <JellyIcon name="arrow-right" className="ml-auto size-3 text-ink/30" />
            </button>
          ))}
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-2.5 rounded-2xl px-2 py-3 text-left text-sm font-semibold text-ink/70 transition-colors duration-150 hover:bg-white"
          >
            <JellyIcon name="circle-question" className="size-4 text-ink/45" />
            Nápověda a podpora
            <JellyIcon name="arrow-right" className="ml-auto size-3 text-ink/30" />
          </button>
        </div>
      </section>

      <section className="animate-rise-up mt-8" style={{ animationDelay: '240ms' }}>
        <Link
          href="/prihlaseni"
          className="flex w-full cursor-pointer items-center gap-2.5 rounded-2xl px-2 py-3 text-left text-sm font-semibold text-ink/70 transition-colors duration-150 hover:bg-white"
        >
          <JellyIcon name="arrow-right-from-bracket" className="size-4 text-ink/45" />
          Odhlásit se
        </Link>

        {/* The TOS promises cancellation anytime with a week's notice, and the
            marketing sells "bez závazků" — hiding it in the app would make both
            a lie. Quiet, but present and honest about the notice period. */}
        <div className="mt-4 border-t border-ink/10 pt-4">
          <button
            type="button"
            className="cursor-pointer text-xs font-bold text-brand underline-offset-4 hover:underline"
          >
            Ukončit spolupráci
          </button>
          <p className="mt-1.5 text-[10px] leading-relaxed font-semibold text-ink/45">
            Výpovědní lhůta je týden. Do konce zaplaceného období pro vás běžíme dál a fotky vám
            zůstanou ke stažení.
          </p>
        </div>

        <p className="mt-6 text-center text-[10px] font-semibold text-ink/30">Bitez 2.0.1</p>
      </section>
    </div>
  )
}
